import React from 'react';

/**
 * Button variant styles matching the existing domain card theming
 */
const VARIANTS = {
  // Standard purple domain card button - with border glow on hover
  primary: 'bg-purple-900/20 text-purple-400 border border-purple-600 hover:bg-purple-900/40 hover:border-purple-400 hover:shadow-md hover:shadow-purple-500/30',
  // Secondary action (less prominent) - with border glow
  secondary: 'bg-gray-700 text-gray-400 border border-gray-600 hover:bg-gray-600 hover:border-gray-400 hover:shadow-md hover:shadow-gray-500/30',
  // Attack/damage action (orange) - with border glow
  attack: 'bg-orange-900/30 text-orange-400 border border-orange-600 hover:bg-orange-900/50 hover:border-orange-400 hover:shadow-md hover:shadow-orange-500/30',
  // Currently active state (filled purple)
  active: 'bg-purple-700 text-purple-200 border border-purple-500',
  // Success/healing active state (green)
  activeSuccess: 'bg-green-700 text-green-200 border border-green-500',
  // Yellow/warning variant - with border glow
  warning: 'bg-yellow-900/30 text-yellow-400 border border-yellow-600 hover:bg-yellow-900/50 hover:border-yellow-400 hover:shadow-md hover:shadow-yellow-500/30',
  // Disabled state
  disabled: 'bg-gray-700 text-gray-500 cursor-not-allowed border border-gray-600',
};

/**
 * DomainCardButton - Standardized button for domain card actions
 *
 * @param {function} onClick - Click handler
 * @param {boolean} disabled - Whether button is disabled
 * @param {boolean} isActive - Whether effect is currently active
 * @param {string} label - Button label when inactive
 * @param {string} activeLabel - Button label when active (optional)
 * @param {string} disabledLabel - Button label when disabled (optional, defaults to label)
 * @param {string} variant - Style variant (primary, secondary, attack, active, warning)
 * @param {string} activeVariant - Style variant when active (defaults to 'active')
 * @param {string} title - Tooltip text
 * @param {string} className - Additional CSS classes
 */
const DomainCardButton = ({
  onClick,
  disabled = false,
  isActive = false,
  label,
  activeLabel,
  disabledLabel,
  variant = 'primary',
  activeVariant = 'active',
  title,
  className = '',
  children, // Allow children as alternative to label prop
}) => {
  // Determine display label
  const displayLabel = isActive
    ? (activeLabel || label)
    : (disabled && disabledLabel)
      ? disabledLabel
      : label;

  // Determine style variant
  const effectiveVariant = disabled
    ? 'disabled'
    : isActive
      ? activeVariant
      : variant;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1 text-xs rounded font-semibold transition-all ${VARIANTS[effectiveVariant]} ${className}`}
      title={title}
    >
      {children || displayLabel}
    </button>
  );
};

/**
 * DualButtonGroup - Two buttons side by side (for dual-mode cards)
 *
 * @param {string} primaryLabel - Primary button label
 * @param {string} primaryVariant - Primary button variant (primary, active, disabled)
 * @param {boolean} primaryDisabled - Whether primary button is disabled
 * @param {string} primaryTitle - Primary button tooltip
 * @param {function} onPrimaryClick - Primary button click handler
 * @param {string} secondaryLabel - Secondary button label
 * @param {string} secondaryVariant - Secondary button variant (attack, secondary, disabled)
 * @param {boolean} secondaryDisabled - Whether secondary button is disabled
 * @param {string} secondaryTitle - Secondary button tooltip
 * @param {function} onSecondaryClick - Secondary button click handler
 */
export const DualButtonGroup = ({
  primaryLabel,
  primaryVariant = 'primary',
  primaryDisabled = false,
  primaryTitle,
  onPrimaryClick,
  secondaryLabel,
  secondaryVariant = 'attack',
  secondaryDisabled = false,
  secondaryTitle,
  onSecondaryClick,
  className = '',
}) => (
  <div className={`flex flex-wrap gap-2 ${className}`}>
    <button
      onClick={onPrimaryClick}
      disabled={primaryDisabled}
      className={`px-3 py-1 text-xs rounded font-semibold transition-all ${VARIANTS[primaryDisabled ? 'disabled' : primaryVariant]}`}
      title={primaryTitle}
    >
      {primaryLabel}
    </button>
    <button
      onClick={onSecondaryClick}
      disabled={secondaryDisabled}
      className={`px-3 py-1 text-xs rounded font-semibold transition-all ${VARIANTS[secondaryDisabled ? 'disabled' : secondaryVariant]}`}
      title={secondaryTitle}
    >
      {secondaryLabel}
    </button>
  </div>
);

/**
 * SplitButton - Main action with toggle (like Gifted Tracker)
 * Supports different color variants for the main button
 */
export const SplitButton = ({
  mainLabel,
  onMainClick,
  mainDisabled = false,
  mainVariant = 'primary',
  mainTitle,
  toggleActive = false,
  toggleIcon,
  onToggleClick,
  toggleTitle,
}) => {
  // Border and divider colors based on variant
  const variantColors = {
    primary: { border: 'border-purple-600', divider: 'bg-purple-600', main: 'bg-purple-900/20 text-purple-400 hover:bg-purple-900/40', toggleActive: 'bg-purple-700 text-purple-200', toggleInactive: 'bg-purple-900/20 text-purple-400 hover:bg-purple-900/40' },
    attack: { border: 'border-orange-600', divider: 'bg-orange-600', main: 'bg-orange-900/30 text-orange-400 hover:bg-orange-900/50', toggleActive: 'bg-orange-700 text-orange-200', toggleInactive: 'bg-orange-900/30 text-orange-400 hover:bg-orange-900/50' },
    warning: { border: 'border-yellow-600', divider: 'bg-yellow-600', main: 'bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50', toggleActive: 'bg-yellow-700 text-yellow-200', toggleInactive: 'bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50' },
    secondary: { border: 'border-gray-600', divider: 'bg-gray-600', main: 'bg-gray-700 text-gray-400 hover:bg-gray-600', toggleActive: 'bg-gray-600 text-gray-200', toggleInactive: 'bg-gray-700 text-gray-400 hover:bg-gray-600' }
  };

  const colors = variantColors[mainVariant] || variantColors.primary;

  return (
    <div className={`flex rounded overflow-hidden border ${colors.border}`}>
      {/* Main action button */}
      <button
        onClick={onMainClick}
        disabled={mainDisabled}
        className={`px-3 py-1 text-xs font-semibold transition-all ${
          mainDisabled
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : colors.main
        }`}
        title={mainTitle}
      >
        {mainLabel}
      </button>
      {/* Divider */}
      <div className={`w-px ${colors.divider}`} />
      {/* Toggle button */}
      <button
        onClick={onToggleClick}
        className={`px-2 py-1 text-xs font-semibold transition-all ${
          toggleActive ? colors.toggleActive : colors.toggleInactive
        }`}
        title={toggleTitle}
      >
        {toggleIcon}
      </button>
    </div>
  );
};

/**
 * SplitButtonWithSelect - Main action button with integrated dropdown selector
 * Creates a unified visual element (like Strange Patterns: toggle + number selector)
 *
 * @param {string} buttonLabel - Label for the main button
 * @param {function} onButtonClick - Click handler for main button
 * @param {string} buttonVariant - Style variant for button (primary, attack, warning)
 * @param {string} buttonTitle - Tooltip for button
 * @param {boolean} buttonDisabled - Whether button is disabled
 * @param {any} selectValue - Current value of the dropdown
 * @param {function} onSelectChange - Change handler for dropdown (receives event)
 * @param {Array} selectOptions - Array of options: [{ value, label }] or [value, ...]
 * @param {string} selectTitle - Tooltip for dropdown
 */
export const SplitButtonWithSelect = ({
  buttonLabel,
  onButtonClick,
  buttonVariant = 'primary',
  buttonTitle,
  buttonDisabled = false,
  selectValue,
  onSelectChange,
  selectOptions = [],
  selectTitle,
}) => {
  // Color configurations for each variant
  const variantColors = {
    primary: {
      border: 'border-purple-600',
      divider: 'bg-purple-600',
      button: 'bg-purple-900/20 text-purple-400 hover:bg-purple-900/40',
      select: 'bg-purple-900/20 text-purple-400 focus:ring-purple-500'
    },
    attack: {
      border: 'border-orange-600',
      divider: 'bg-orange-600',
      button: 'bg-orange-900/30 text-orange-400 hover:bg-orange-900/50',
      select: 'bg-orange-900/30 text-orange-400 focus:ring-orange-500'
    },
    warning: {
      border: 'border-yellow-600',
      divider: 'bg-yellow-600',
      button: 'bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50',
      select: 'bg-yellow-900/30 text-yellow-400 focus:ring-yellow-500'
    },
    secondary: {
      border: 'border-gray-600',
      divider: 'bg-gray-600',
      button: 'bg-gray-700 text-gray-400 hover:bg-gray-600',
      select: 'bg-gray-700 text-gray-400 focus:ring-gray-500'
    }
  };

  const colors = variantColors[buttonVariant] || variantColors.primary;

  return (
    <div className={`flex rounded overflow-hidden border ${colors.border}`}>
      {/* Main action button */}
      <button
        onClick={onButtonClick}
        disabled={buttonDisabled}
        className={`px-3 py-1 text-xs font-semibold transition-all ${
          buttonDisabled
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : colors.button
        }`}
        title={buttonTitle}
      >
        {buttonLabel}
      </button>
      {/* Divider */}
      <div className={`w-px ${colors.divider}`} />
      {/* Select dropdown */}
      <select
        value={selectValue}
        onChange={onSelectChange}
        className={`px-2 py-1 text-xs font-semibold cursor-pointer border-0 rounded-none
                    focus:outline-none focus:ring-2 focus:ring-inset ${colors.select}`}
        title={selectTitle}
      >
        {selectOptions.map((opt) => {
          const value = opt.value !== undefined ? opt.value : opt;
          const label = opt.label !== undefined ? opt.label : opt;
          return (
            <option key={value} value={value}>
              {label}
            </option>
          );
        })}
      </select>
    </div>
  );
};

export default DomainCardButton;
